import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

const AppSyncContext = createContext(null);

const STORAGE_KEY = 'ariaone-sync';
const ROLE_KEY = 'ariaone-user-role';
const NOTIF_KEY = 'ariaone-notifications';

/* ── Default shared state ── */
const DEFAULT_STATE = {
  patients: {
    'P001': { id: 'P001', name: 'John Smith', status: 'scheduled', room: null, checkedIn: false, soapStatus: null, claimId: null, claimStatus: null },
    'P002': { id: 'P002', name: 'Maria Garcia', status: 'scheduled', room: null, checkedIn: false, soapStatus: null, claimId: null, claimStatus: null },
    'P003': { id: 'P003', name: 'Robert Johnson', status: 'scheduled', room: null, checkedIn: false, soapStatus: null, claimId: null, claimStatus: null },
    'P004': { id: 'P004', name: 'Emily Davis', status: 'roomed', room: 'Exam 2', checkedIn: true, soapStatus: 'completed', claimId: 'CLM-2026-0891', claimStatus: 'submitted' },
    'P005': { id: 'P005', name: 'Sarah Williams', status: 'scheduled', room: null, checkedIn: false, soapStatus: null, claimId: null, claimStatus: null },
  },
  claims: [],
  events: [],
};

/* ── Role → home route mapping ── */
export const ROLE_HOME_ROUTES = {
  provider: '/lumina',
  ma: '/lumina/ma',
  nurse: '/lumina',
  scribe: '/lumina',
  clinical_admin: '/lumina/settings',
  practice_manager: '/revq',
  billing_manager: '/revq',
  biller_coder: '/revq/claims',
  compliance_officer: '/revq/analytics',
  revenue_analyst: '/revq/analytics',
  office_manager: '/echo',
  front_desk: '/echo',
  scheduler: '/echo/schedule',
  intake_coordinator: '/echo/patients',
  medical_records: '/echo/patients',
};

/* ── Role → app mapping ── */
export const ROLE_APP = {
  provider: 'lumina', ma: 'lumina', nurse: 'lumina', scribe: 'lumina', clinical_admin: 'lumina',
  practice_manager: 'revq', billing_manager: 'revq', biller_coder: 'revq', compliance_officer: 'revq', revenue_analyst: 'revq',
  office_manager: 'echo', front_desk: 'echo', scheduler: 'echo', intake_coordinator: 'echo', medical_records: 'echo',
};

/* ── Role → allowed route prefixes ── */
export const ROLE_ALLOWED_ROUTES = {
  provider: ['/lumina', '/landing'],
  ma: ['/lumina/ma', '/lumina/documentation', '/lumina/appointments', '/landing'],
  nurse: ['/lumina', '/landing'],
  scribe: ['/lumina', '/landing'],
  clinical_admin: ['/lumina/settings', '/lumina/credits', '/lumina/integrations', '/landing'],
  practice_manager: ['/revq', '/landing'],
  billing_manager: ['/revq', '/landing'],
  biller_coder: ['/revq/claims', '/revq/encounters', '/revq/denials', '/revq/settings', '/landing'],
  compliance_officer: ['/revq/analytics', '/revq/claims', '/revq/denials', '/revq/settings', '/landing'],
  revenue_analyst: ['/revq/analytics', '/revq/payments', '/revq', '/landing'],
  office_manager: ['/echo', '/landing'],
  front_desk: ['/echo', '/landing'],
  scheduler: ['/echo/schedule', '/echo/patients', '/echo/settings', '/landing'],
  intake_coordinator: ['/echo/patients', '/echo/schedule', '/echo', '/landing'],
  medical_records: ['/echo/patients', '/echo', '/landing'],
};

const loadState = () => {
  try { const s = localStorage.getItem(STORAGE_KEY); return s ? { ...DEFAULT_STATE, ...JSON.parse(s) } : DEFAULT_STATE; }
  catch { return DEFAULT_STATE; }
};

const loadNotifications = () => {
  try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || '[]'); }
  catch { return []; }
};

export const AppSyncProvider = ({ children }) => {
  const [syncState, setSyncState] = useState(loadState);
  const [userRole, setUserRole] = useState(() => localStorage.getItem(ROLE_KEY) || 'provider');
  const [notifications, setNotifications] = useState(loadNotifications);
  const syncRef = useRef(syncState);

  // Persist state
  useEffect(() => { syncRef.current = syncState; localStorage.setItem(STORAGE_KEY, JSON.stringify(syncState)); }, [syncState]);
  useEffect(() => { localStorage.setItem(ROLE_KEY, userRole); }, [userRole]);
  useEffect(() => { localStorage.setItem(NOTIF_KEY, JSON.stringify(notifications.slice(0, 50))); }, [notifications]);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try { setSyncState(JSON.parse(e.newValue)); } catch {}
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const addNotification = useCallback((notif) => {
    const n = { id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`, time: new Date().toISOString(), read: false, ...notif };
    setNotifications(prev => [n, ...prev]);
    return n;
  }, []);

  const markNotifRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const clearNotifications = useCallback(() => setNotifications([]), []);

  /* ════════════════════════════════════
     CROSS-APP SYNC ACTIONS
     ════════════════════════════════════ */

  // Lumina → Echo: Room a patient
  const roomPatient = useCallback((patientId, room, workflow) => {
    setSyncState(prev => {
      const patient = prev.patients[patientId];
      if (!patient) return prev;
      const updated = { ...prev, patients: { ...prev.patients, [patientId]: { ...patient, status: 'roomed', room, checkedIn: true } } };
      return updated;
    });
    const patient = syncRef.current.patients[patientId];
    const name = patient?.name || 'Patient';
    addNotification({ source: 'lumina', target: 'echo', type: 'patient_roomed', message: `${name} roomed in ${room}`, icon: 'door', patientId });
    toast.success(`${name} roomed in ${room}`, { description: 'Echo patient flow updated' });
  }, [addNotification]);

  // Echo → Lumina: Check in a patient
  const checkInPatient = useCallback((patientId) => {
    setSyncState(prev => {
      const patient = prev.patients[patientId];
      if (!patient) return prev;
      return { ...prev, patients: { ...prev.patients, [patientId]: { ...patient, status: 'checked-in', checkedIn: true } } };
    });
    const patient = syncRef.current.patients[patientId];
    const name = patient?.name || 'Patient';
    addNotification({ source: 'echo', target: 'lumina', type: 'patient_checked_in', message: `${name} checked in at front desk`, icon: 'check', patientId });
    toast.success(`${name} checked in`, { description: 'Lumina patient list updated' });
  }, [addNotification]);

  // Lumina → RevQ: SOAP note signed & sent to billing
  const sendToBilling = useCallback((patientId, noteData) => {
    const claimId = `CLM-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`;
    setSyncState(prev => {
      const patient = prev.patients[patientId];
      if (!patient) return prev;
      const claim = {
        id: claimId, patientId, patientName: patient.name, status: 'submitted',
        amount: noteData?.amount || `$${(Math.random() * 500 + 100).toFixed(2)}`,
        cpt: noteData?.cpt || ['99214'], icd: noteData?.icd || ['E11.9'],
        provider: noteData?.provider || 'Dr. Sarah Chen',
        dateOfService: new Date().toISOString().split('T')[0],
        submittedAt: new Date().toISOString(),
      };
      return {
        ...prev,
        patients: { ...prev.patients, [patientId]: { ...patient, soapStatus: 'sent-to-billing', claimId, claimStatus: 'submitted' } },
        claims: [...prev.claims, claim],
      };
    });
    const patient = syncRef.current.patients[patientId];
    const name = patient?.name || 'Patient';
    addNotification({ source: 'lumina', target: 'revq', type: 'claim_submitted', message: `Claim ${claimId} submitted for ${name}`, icon: 'send', patientId, claimId });
    toast.success(`Claim ${claimId} sent to RevQ`, { description: `${name} — billing pipeline updated` });
    return claimId;
  }, [addNotification]);

  // RevQ → Lumina: Claim status update
  const updateClaimStatus = useCallback((claimId, newStatus) => {
    setSyncState(prev => {
      const claimIdx = prev.claims.findIndex(c => c.id === claimId);
      if (claimIdx === -1) return prev;
      const claim = prev.claims[claimIdx];
      const updatedClaims = [...prev.claims];
      updatedClaims[claimIdx] = { ...claim, status: newStatus };
      const updatedPatients = { ...prev.patients };
      if (claim.patientId && updatedPatients[claim.patientId]) {
        updatedPatients[claim.patientId] = { ...updatedPatients[claim.patientId], claimStatus: newStatus };
      }
      return { ...prev, claims: updatedClaims, patients: updatedPatients };
    });
    addNotification({ source: 'revq', target: 'lumina', type: 'claim_status_update', message: `Claim ${claimId} → ${newStatus}`, icon: 'billing', claimId });
    toast.success(`Claim ${claimId} updated to ${newStatus}`, { description: 'Lumina billing status synced' });
  }, [addNotification]);

  // Echo → Lumina: Schedule change
  const updateSchedule = useCallback((patientId, change) => {
    addNotification({ source: 'echo', target: 'lumina', type: 'schedule_change', message: `Schedule updated: ${change}`, icon: 'calendar', patientId });
    toast.info(`Schedule updated`, { description: change });
  }, [addNotification]);

  // Lumina → Echo: Patient ready for checkout
  const markReadyForCheckout = useCallback((patientId) => {
    setSyncState(prev => {
      const patient = prev.patients[patientId];
      if (!patient) return prev;
      return { ...prev, patients: { ...prev.patients, [patientId]: { ...patient, status: 'checkout-ready' } } };
    });
    const patient = syncRef.current.patients[patientId];
    addNotification({ source: 'lumina', target: 'echo', type: 'checkout_ready', message: `${patient?.name || 'Patient'} ready for checkout`, icon: 'check', patientId });
    toast.success(`${patient?.name} marked for checkout`, { description: 'Echo front desk notified' });
  }, [addNotification]);

  // RevQ → Echo: Patient balance alert
  const alertPatientBalance = useCallback((patientId, amount) => {
    const patient = syncRef.current.patients[patientId];
    addNotification({ source: 'revq', target: 'echo', type: 'balance_alert', message: `${patient?.name || 'Patient'} has outstanding balance: ${amount}`, icon: 'billing', patientId });
    toast.info(`Patient balance alert`, { description: `${patient?.name}: ${amount} outstanding` });
  }, [addNotification]);

  const getPatient = useCallback((patientId) => syncState.patients[patientId] || null, [syncState]);
  const getClaims = useCallback(() => syncState.claims, [syncState]);
  const getClaimsByPatient = useCallback((patientId) => syncState.claims.filter(c => c.patientId === patientId), [syncState]);
  const getUnreadCount = useCallback(() => notifications.filter(n => !n.read).length, [notifications]);
  const getNotificationsForApp = useCallback((app) => notifications.filter(n => n.target === app || n.source === app), [notifications]);

  return (
    <AppSyncContext.Provider value={{
      // State
      syncState, userRole, notifications,
      // Setters
      setUserRole,
      // Patient actions
      roomPatient, checkInPatient, sendToBilling, updateClaimStatus, updateSchedule,
      markReadyForCheckout, alertPatientBalance,
      // Queries
      getPatient, getClaims, getClaimsByPatient,
      // Notifications
      addNotification, markNotifRead, clearNotifications, getUnreadCount, getNotificationsForApp,
      // Role helpers
      getRoleHome: (role) => ROLE_HOME_ROUTES[role] || '/landing',
      getRoleApp: (role) => ROLE_APP[role] || 'lumina',
      isRouteAllowed: (role, path) => {
        const allowed = ROLE_ALLOWED_ROUTES[role];
        if (!allowed) return true;
        return allowed.some(prefix => path.startsWith(prefix)) || path === '/' || path === '/landing' || path === '/setup';
      },
    }}>
      {children}
    </AppSyncContext.Provider>
  );
};

export const useAppSync = () => {
  const ctx = useContext(AppSyncContext);
  if (!ctx) throw new Error('useAppSync must be used within AppSyncProvider');
  return ctx;
};

export default AppSyncContext;
